import { format } from 'date-fns';
import {
  UserHistoryItemDto,
  UserHistoryPaymentMethod,
} from '../../../models/payment/dto/user-history.dto';

/**
 * PaymentIntent statuses that never captured money — same "Incomplet"/
 * "Annulée" (muted) bucket `getStatusBadge` already gives them (D-BADGES).
 * `amountCents` on these rows is `pi.amount`, the amount that WOULD have
 * been charged (see `mapPaymentIntentItem`), not an amount that was — so
 * signing/coloring it like a real debit is misleading. Bug fixed here: the
 * admin table (which intentionally shows these rows, unlike the player-
 * facing H1 noise filter — see `useGetUserHistory`'s doc comment) rendered
 * a canceled attempt in the exact same red "-10.60 €" as a genuinely paid
 * row, with nothing but the status badge to tell them apart.
 */
const UNCHARGED_PAYMENT_STATUSES = new Set([
  'canceled',
  'requires_action',
  'requires_capture',
  'requires_confirmation',
  'requires_payment_method',
]);

/**
 * Player-perspective amount sign (epic D-SIGN, both surfaces): payments are
 * debits (negative), refunds/credits are positive. `kind: 'wallet'` rows
 * (standalone ADMIN_ADJUSTMENT/CAMPAIGN_INCENTIVE/ACHIEVEMENT_REWARD — the
 * dedup truth table absorbs SPEND/PURCHASE into their PI row) take their
 * sign from `walletDelta`, which is already signed by the wallet ledger.
 * `kind: 'activity'` rows are informational participations (the epic's
 * Source 4/5 "no money moved on their side" rows) — never colored/signed,
 * even when they carry a non-zero `amountCents` (a Duo beneficiary's share,
 * shown for context only; the real charge is the payer's own PI/invoice row).
 */
export function getAmountSign(item: UserHistoryItemDto): 1 | -1 | 0 {
  if (item.kind === 'payment') {
    if (!item.amountCents) return 0;
    return UNCHARGED_PAYMENT_STATUSES.has(item.status) ? 0 : -1;
  }
  if (item.kind === 'refund') return item.amountCents ? 1 : 0;
  if (item.kind === 'wallet') {
    if (item.walletDelta == null || item.walletDelta === 0) return 0;
    return item.walletDelta > 0 ? 1 : -1;
  }
  return 0;
}

/** `-10.00 €` / `+10.00 €` / `10.00 €` (sign 0) / `—` (unknown amount, M4/M5: never conflate null with free/0). */
export function formatSignedAmount(cents: number | null | undefined, sign: 1 | -1 | 0): string {
  if (cents == null) return '—';
  const prefix = sign > 0 ? '+' : sign < 0 ? '-' : '';
  return `${prefix}${(cents / 100).toFixed(2)} €`;
}

/**
 * Muted credits annotation for a wallet leg, e.g. "10 crédits". Null when the
 * row carries no wallet leg, OR when `walletCreditRate` is exactly 1 (D-COPY:
 * "parens dropped when rate=1 renders identical numbers" — 1 credit == 1 €,
 * so the annotation would repeat the same digits already shown as the
 * primary € amount). `walletCreditRate` is only ever set on a SPEND-linked PI
 * row (see `mapPaymentIntentItem`); standalone wallet rows (ADMIN_ADJUSTMENT
 * etc.) always carry `walletCreditRate: null` and have no `amountCents` at
 * all, so this never suppresses their only amount info.
 */
export function formatCreditsAnnotation(
  walletDelta: number | null | undefined,
  walletCreditRate?: number | null,
): string | null {
  if (walletDelta == null) return null;
  if (walletCreditRate != null && Math.abs(walletCreditRate - 1) < 1e-9) return null;
  const abs = Math.abs(walletDelta);
  return `${formatCreditsNumber(abs)} crédit${abs === 1 ? '' : 's'}`;
}

/** "10" / "10.5" — shared integer/decimal formatting for a raw credits count (no sign, no unit word). */
function formatCreditsNumber(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
}

/** Solde column: credits balance after a wallet leg, em-dash otherwise (D-SIGN F5). */
export function formatBalanceAfter(walletBalanceAfter: number | null | undefined): string {
  if (walletBalanceAfter == null) return '—';
  return Number.isInteger(walletBalanceAfter)
    ? walletBalanceAfter.toString()
    : walletBalanceAfter.toFixed(2);
}

export type StatusBadge = { label: string; colorScheme: string };

const PAYMENT_STATUS_BADGES: Record<string, StatusBadge> = {
  succeeded: { label: 'Payée', colorScheme: 'green' },
  pay_later: { label: 'À régler', colorScheme: 'orange' },
  refunded: { label: 'Remboursée', colorScheme: 'orange' },
  refunding: { label: 'Remboursement…', colorScheme: 'yellow' },
  canceled: { label: 'Annulée', colorScheme: 'gray' },
  processing: { label: 'En cours', colorScheme: 'yellow' },
  requires_action: { label: 'Incomplet', colorScheme: 'gray' },
  requires_capture: { label: 'Incomplet', colorScheme: 'gray' },
  requires_confirmation: { label: 'Incomplet', colorScheme: 'gray' },
  requires_payment_method: { label: 'Incomplet', colorScheme: 'gray' },
};

const REFUND_STATUS_BADGES: Record<string, StatusBadge> = {
  SUCCESSFUL: { label: 'Remboursée', colorScheme: 'green' },
  RETRY: { label: 'Remboursement…', colorScheme: 'yellow' },
  FAILED: { label: 'Échec', colorScheme: 'red' },
};

/**
 * Status badge for a row (epic D-BADGES, first pass — the finer per-surface
 * palette/copy work is T5@design (dashboard half) / T6@design (dashboard
 * half), still open on the ledger; this gives every row a defensible badge
 * now instead of leaving the column blank).
 *
 * `refunds` (plural, on PaymentIntent rows only) is the "original badge
 * status" signal from T6@design-review's DTO note: a partial refund never
 * flips `status` away from 'succeeded' (only a FULL refund does — see
 * refund-game.ts), so it is the only way this column can tell "paid" from
 * "paid, then partially refunded" without a second round trip to Source 2.
 */
export function getStatusBadge(item: UserHistoryItemDto): StatusBadge {
  if (item.kind === 'payment') {
    if (item.paymentMethod === 'free') return { label: 'Inclus (abo)', colorScheme: 'gray' };
    if (item.paymentMethod === 'manual') return { label: 'Réglée sur place', colorScheme: 'teal' };
    if (item.status === 'succeeded' && item.refunds?.length) {
      const hasFullRefund = item.refunds.some((r) => r.amountCents === null);
      return hasFullRefund
        ? { label: 'Remboursée', colorScheme: 'orange' }
        : { label: 'Remb. partiel', colorScheme: 'orange' };
    }
    return PAYMENT_STATUS_BADGES[item.status] ?? { label: item.status, colorScheme: 'gray' };
  }
  if (item.kind === 'refund') {
    return REFUND_STATUS_BADGES[item.status] ?? { label: item.status, colorScheme: 'gray' };
  }
  // wallet / activity rows have no D-BADGES entry of their own — the epic
  // doesn't define one, so show the raw source status muted rather than
  // inventing a mapping.
  return { label: item.status, colorScheme: 'gray' };
}

const PAYMENT_METHOD_LABELS: Record<UserHistoryPaymentMethod, string> = {
  wallet: 'Wallet',
  card: 'Carte',
  manual: 'Manuel',
  free: 'Inclus',
  pay_later: 'À régler',
};

export function getPaymentMethodLabel(method: UserHistoryPaymentMethod | undefined): string {
  if (!method) return '—';
  return PAYMENT_METHOD_LABELS[method] ?? method;
}

/** "12/08 18:30" — sanctioned deviation from the dashboard's relative-time convention (epic D-DATES: financial rows use absolute dates). */
export function formatOccurredAt(iso: string): string {
  return format(new Date(iso), 'dd/MM HH:mm');
}

/** Full tooltip date, e.g. "12/08/2026 à 18:30:05". */
export function formatOccurredAtFull(iso: string): string {
  return format(new Date(iso), "dd/MM/yyyy 'à' HH:mm:ss");
}

/**
 * "12/08" — the secondary/activity date (D-DATES: dual dates. Row primary
 * date is `occurredAt`, the transaction date; this is `linkage.activityStartTime`,
 * the play/booking date, rendered muted next to the item label — mirrors
 * mobile's L1 "<label> · match du 12/08" placement rather than the epic's
 * literal game-only example, since a row's linkage can be a court booking,
 * event, coaching session, or membership, not only a game).
 */
export function formatActivityDate(iso: string): string {
  return format(new Date(iso), 'dd/MM');
}

/**
 * "12/08" — the original-payment sub-line date for a refund row (D-REFUND:
 * "Refund = own row, positive, sub-line 'sur le paiement du 10/08'").
 * `UserHistoryRefundInfoDto` (the `refund` field on a refund-kind item)
 * carries the original PaymentIntent's ID but NOT its date — the read model
 * doesn't round-trip that (see the dto's field comment) — so this is a
 * best-effort correlation against whatever pages are ALREADY LOADED in this
 * session, keyed by the namespaced `pi:<id>` item id, not a second API call.
 * Returns null (never a placeholder) when the original payment hasn't been
 * fetched yet — a different page, or excluded by the current kind/date
 * filter — same "only render what's truthfully resolvable" discipline
 * `formatActivityDate`'s own gate on `linkage.activityStartTime` uses, and
 * gated to `kind === 'refund'` only: the epic's D-REFUND sentence describes
 * the payer's own refund row, not the covered-user `kind: 'activity'`
 * view, which already carries its note in `label` (T6@design, api half).
 */
export function resolveRefundOriginalPaymentDate(
  item: UserHistoryItemDto,
  itemsById: Map<string, UserHistoryItemDto>,
): string | null {
  if (item.kind !== 'refund' || !item.refund?.originalPaymentIntentId) return null;
  const original = itemsById.get(`pi:${item.refund.originalPaymentIntentId}`);
  if (!original) return null;
  return formatActivityDate(original.occurredAt);
}

/** Group key for the day header rows — "2026-08-12" (stable sort key, not the display string). */
export function dayGroupKey(iso: string): string {
  return format(new Date(iso), 'yyyy-MM-dd');
}

/**
 * "12 août 2026" day header label. Uses `toLocaleDateString('fr-FR', ...)`
 * rather than date-fns's `format` + a `locale` option deliberately:
 * `date-fns/locale/fr` ships ESM that Jest's CRA transform cannot parse
 * (see `SharedCalendar.test.ts`'s `jest.mock('date-fns/locale/fr', ...)`
 * workaround elsewhere in this repo) — `toLocaleDateString` needs no import
 * and matches the server's own `formatShortDate`
 * (resolve-payment-intent-linkage.ts) convention.
 */
export function formatDayHeader(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Groups already-sorted (DESC by occurredAt) items into contiguous day
 * buckets. Does NOT re-sort — a cursor page from the API is already ordered,
 * and re-sorting client-side would silently hide a keyset/dedup bug instead
 * of surfacing it.
 */
export type DayGroup = { key: string; label: string; items: UserHistoryItemDto[] };

export function groupItemsByDay(items: UserHistoryItemDto[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const item of items) {
    const key = dayGroupKey(item.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(item);
    } else {
      groups.push({ key, label: formatDayHeader(item.occurredAt), items: [item] });
    }
  }
  return groups;
}

/**
 * Client-side search over the currently loaded pages (amount + label, per
 * epic F3 "debounced search reused from TransactionHistoryTable" — the
 * unified-history endpoints don't take a `search` param the way
 * `admin/transactions` does, so this filters what's already fetched rather
 * than adding a round trip).
 */
export function matchesSearch(item: UserHistoryItemDto, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (item.label.toLowerCase().includes(q)) return true;
  if (item.amountCents != null && (item.amountCents / 100).toFixed(2).includes(q)) return true;
  return false;
}

/**
 * "12/08/2026" — full date for a `<input type=date>` value (`dateFrom`/
 * `dateTo`, always a bare "YYYY-MM-DD" string, never an ISO timestamp), used
 * for the D-STATES filter-empty chip labels below. `T00:00:00` mirrors the
 * component's own local-midnight parsing for these inputs (see
 * `UserHistorySection.tsx`'s `isoDateFrom`/`isoDateTo`).
 */
export function formatFilterDate(dateInputValue: string): string {
  return format(new Date(`${dateInputValue}T00:00:00`), 'dd/MM/yyyy');
}

/**
 * "12/08/2026" — the import date shown in D-STATES' PARTIAL-state import
 * banner (T7@design-followup). Full day/month/year, unlike the abbreviated
 * `formatActivityDate`, since this date stands alone in a banner rather than
 * next to an already-dated row.
 */
export function formatImportedAtDate(iso: string): string {
  return format(new Date(iso), 'dd/MM/yyyy');
}

/**
 * Composed accessible sentence for one row (D-A11Y, T7@design dashboard
 * half: "each row = one accessible element with a composed label"). Mirrors
 * the epic's own illustrative example ("Réservation Padel 3, payée dix
 * euros le dix août par wallet, solde quarante crédits") but with digits
 * rather than spelled-out numbers — no French number-to-words converter
 * exists in this codebase, every other translated string here uses digits,
 * and digits remain fully legible to screen readers.
 */
export function buildRowAccessibleLabel(item: UserHistoryItemDto): string {
  const sign = getAmountSign(item);
  const parts: string[] = [item.label];

  if (item.kind === 'wallet') {
    const abs = Math.abs(item.walletDelta ?? 0);
    parts.push(
      sign > 0
        ? `crédité de ${formatCreditsNumber(abs)} crédits`
        : sign < 0
          ? `débité de ${formatCreditsNumber(abs)} crédits`
          : 'aucun mouvement',
    );
  } else if (item.amountCents == null) {
    parts.push('montant inconnu');
  } else if (item.kind === 'refund') {
    parts.push(`remboursé de ${(item.amountCents / 100).toFixed(2)} €`);
  } else if (item.kind === 'payment') {
    // sign === 0 here means an uncharged status (canceled/requires_*, see
    // UNCHARGED_PAYMENT_STATUSES) — "payé" would contradict the "Annulée"/
    // "Incomplet" badge pushed below, so drop the verb rather than assert a
    // charge that never happened.
    const amountStr = `${(item.amountCents / 100).toFixed(2)} € par ${getPaymentMethodLabel(item.paymentMethod)}`;
    parts.push(sign === 0 ? amountStr : `payé ${amountStr}`);
  } else {
    // kind === 'activity': informational participation, never signed/colored
    // (see getAmountSign's own doc comment) — the amount shown is context only.
    parts.push(`activité, ${(item.amountCents / 100).toFixed(2)} €`);
  }

  parts.push(`le ${formatOccurredAt(item.occurredAt)}`);
  if (item.walletBalanceAfter != null) {
    parts.push(`solde ${formatCreditsNumber(item.walletBalanceAfter)} crédits`);
  }
  parts.push(getStatusBadge(item).label);

  return parts.join(', ');
}
